import { LanguageKeys } from 'src/common/constants/languages';
import { EUserOrderBy } from 'src/common/constants/userSortKeys';
import { filterUsers } from 'src/helpers/filterUsers';
import {
  getParticipantRegistrationState,
  matchesParticipantQuery,
} from 'src/helpers/participantList';
import { getRegistrationDisplayPrice } from 'src/helpers/registrationPrice';
import { ICheckin } from 'src/interfaces/Checkin';
import { IBaseEdition } from 'src/interfaces/Edition';
import { EOrder } from 'src/interfaces/Order';
import { IEnrichedParticipant, IParticipant } from 'src/interfaces/Participant';
import { IBaseTechnicalType } from 'src/interfaces/TechnicalType';
import { IUser } from 'src/interfaces/User';

export type WizardRegistrationStatus = 'unregistered' | 'registered' | 'checkedIn';

export type WizardPriceBreakdown = {
  checkinsByPrice: Record<string, number>;
  totalPrice: number;
};

export type WizardSummaryRow = {
  participantId: string;
  participantName: string;
  competitionLabel: string;
  distanceLabel: string;
  ser: number | string;
  price: number;
};

export type WizardRegisterDraft = {
  competitionId: string;
  distanceId: string;
  price: number;
};

/** Per-participant check-in draft for already-registered participants. */
export type WizardCheckinDraft = {
  competitionId: string;
  distanceId: string;
  price: number;
};

export function filterParticipantsByOwner(
  participants: IParticipant[],
  ownerUserId: string,
): IParticipant[] {
  if (!ownerUserId) {
    return [];
  }
  const ownerKey = String(ownerUserId);
  return participants.filter(p => p.ownerUserId != null && String(p.ownerUserId) === ownerKey);
}

export function isUnownedParticipant(participant: Pick<IParticipant, 'ownerUserId'>): boolean {
  return participant.ownerUserId == null || participant.ownerUserId === '';
}

export function filterUnownedParticipants<T extends Pick<IParticipant, 'ownerUserId'>>(
  participants: T[],
): T[] {
  return participants.filter(isUnownedParticipant);
}

export type WizardFindOption =
  | { kind: 'user'; id: string; user: IUser }
  | { kind: 'participant'; id: string; participant: IEnrichedParticipant };

export function wizardFindOptionLabel(option: WizardFindOption): string {
  if (option.kind === 'user') {
    return `${option.user.name} (${option.user.email})`;
  }
  const name = `${option.participant.firstName} ${option.participant.lastName}`.trim();
  const detail = option.participant.email || option.participant.phoneNumber || '';
  return detail ? `${name} (${detail})` : name;
}

export function buildWizardFindOptions(
  users: IUser[],
  unownedParticipants: IEnrichedParticipant[],
  query: string,
): WizardFindOption[] {
  const filteredUsers = filterUsers(users, {
    query,
    roleFilter: null,
    orderBy: EUserOrderBy.name,
    orderDir: EOrder.asc,
  });
  const userOptions: WizardFindOption[] = filteredUsers.map(user => ({
    kind: 'user',
    id: `user:${user.id}`,
    user,
  }));
  const participantOptions: WizardFindOption[] = unownedParticipants
    .filter(p => matchesParticipantQuery(query.trim(), p))
    .map(participant => ({
      kind: 'participant' as const,
      id: `participant:${participant.id}`,
      participant,
    }))
    .sort((a, b) =>
      wizardFindOptionLabel(a).localeCompare(wizardFindOptionLabel(b), undefined, {
        sensitivity: 'base',
      }),
    );
  return [...userOptions, ...participantOptions];
}

export function getParticipantRegistrationStatus(
  participant: IEnrichedParticipant,
): WizardRegistrationStatus {
  const state = getParticipantRegistrationState(participant);
  return state === 'checked_in' ? 'checkedIn' : state;
}

export function partitionByRegistrationStatus(participants: IEnrichedParticipant[]): {
  unregistered: IEnrichedParticipant[];
  registered: IEnrichedParticipant[];
  checkedIn: IEnrichedParticipant[];
} {
  const unregistered: IEnrichedParticipant[] = [];
  const registered: IEnrichedParticipant[] = [];
  const checkedIn: IEnrichedParticipant[] = [];

  participants.forEach(p => {
    const status = getParticipantRegistrationStatus(p);
    if (status === 'checkedIn') {
      checkedIn.push(p);
    } else if (status === 'registered') {
      registered.push(p);
    } else {
      unregistered.push(p);
    }
  });

  return { unregistered, registered, checkedIn };
}

export function getDefaultSelectedParticipantIds(
  participants: IEnrichedParticipant[],
): string[] {
  return participants
    .filter(p => getParticipantRegistrationStatus(p) !== 'checkedIn')
    .map(p => p.id);
}

export function buildWizardPriceBreakdown(
  items: { price: number }[],
): WizardPriceBreakdown {
  const checkinsByPrice: Record<string, number> = {};
  let totalPrice = 0;

  items.forEach(({ price }) => {
    const key = String(price);
    checkinsByPrice[key] = (checkinsByPrice[key] ?? 0) + 1;
    totalPrice += price;
  });

  return { checkinsByPrice, totalPrice };
}

export function getSummaryPriceItems(
  participants: IEnrichedParticipant[],
  edition?: IBaseEdition,
): { price: number }[] {
  return participants
    .map(p => {
      const reg = p.registration;
      if (!reg) {
        return null;
      }
      const price =
        getRegistrationDisplayPrice(
          reg.price,
          edition,
          {
            dateOfBirth: p.dateOfBirth,
            age: p.age,
            discounted: p.discounted,
          },
        ) ?? reg.price;
      return { price: price ?? 0 };
    })
    .filter((item): item is { price: number } => item != null);
}

export function buildSummaryRows(
  participants: IEnrichedParticipant[],
  checkinsByParticipantId: Record<string, ICheckin | undefined>,
  competitionsById: Record<string, IBaseTechnicalType>,
  distancesById: Record<string, IBaseTechnicalType>,
  lang: LanguageKeys,
): WizardSummaryRow[] {
  return participants.map(p => {
    const reg = p.registration;
    const checkin = p.checkin ?? checkinsByParticipantId[p.id];
    const competitionId = reg?.competitionId ?? checkin?.competition ?? '';
    const distanceId = reg?.distanceId ?? checkin?.distance ?? '';
    const competition = competitionsById[competitionId];
    const distance = distancesById[distanceId];

    return {
      participantId: p.id,
      participantName: `${p.lastName} ${p.firstName}`,
      competitionLabel: competition?.i18n?.[lang] ?? (competitionId || '-'),
      distanceLabel: distance?.i18n?.[lang] ?? (distanceId || '-'),
      ser: checkin?.ser ?? '-',
      price: reg?.price ?? 0,
    };
  });
}

export function canRegisterParticipant(participant: IEnrichedParticipant): boolean {
  return !!participant.dateOfBirth;
}
